import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Copy, Download, ShieldCheck } from 'lucide-react';
import apiService from '../../utils/apiService';
import { generateQrDataUrl, copyTextToClipboard } from '../../pages/qr/qrHelpers';
import NewtonsCradleLoader from '../common/NewtonsCradleLoader';

const EXPIRED_MESSAGE = 'Your sign-in session has expired. Please sign in again.';

const digitsOnly = (value) => String(value || '').replace(/\D/g, '').slice(0, 6);

/**
 * Second step of partner login when the admin enabled two-factor authentication.
 *
 * Props:
 *  - challengeToken: short-lived token from the login response (kept in memory only, never persisted)
 *  - setupRequired:  true => first-time authenticator setup (QR + confirm + recovery codes); false => enter code
 *  - affiliateCode:  full partner code (used to persist the session after success)
 *  - onBack(message?): return to the password step (optionally with an error message to show there)
 *  - onSuccess(result): called with the same `result` shape a normal login yields ({ success, data: user })
 *
 * Nothing (token/user) is stored until the final success (after recovery codes are acknowledged).
 */
const TwoFactorStep = ({ challengeToken, setupRequired, affiliateCode, onBack, onSuccess }) => {
  const [phase, setPhase] = useState(setupRequired ? 'setup' : 'verify'); // setup | verify | recovery
  const [setupInfo, setSetupInfo] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [code, setCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [pendingResult, setPendingResult] = useState(null);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isPreparing, setIsPreparing] = useState(!!setupRequired);
  const setupRequestedRef = useRef(false);

  const handleFailure = (res) => {
    const errorCode = res?.data?.errorCode;
    if (errorCode === 'TWO_FACTOR_CHALLENGE_INVALID') {
      onBack(res.message || EXPIRED_MESSAGE);
      return;
    }
    if (errorCode === 'TWO_FACTOR_LOCKED') {
      setError(res.message || 'Too many failed attempts. Please try again later.');
    } else if (errorCode === 'TWO_FACTOR_INVALID') {
      setError(
        useRecovery && phase === 'verify'
          ? 'Invalid recovery code. Please check it and try again.'
          : 'Invalid code. Please check your authenticator app and try again.'
      );
    } else {
      setError(res?.error || res?.message || 'Verification failed. Please try again.');
    }
  };

  // First-time setup: ask the server for a fresh secret once (React StrictMode runs effects twice in dev,
  // and every call regenerates the secret, so guard with a ref).
  useEffect(() => {
    if (!setupRequired || setupRequestedRef.current) return;
    setupRequestedRef.current = true;

    (async () => {
      try {
        const res = await apiService.twoFactorSetup(challengeToken);
        if (!res.success || !res.data) {
          handleFailure(res);
          return;
        }
        setSetupInfo(res.data);
        try {
          setQrDataUrl(await generateQrDataUrl(res.data.otpAuthUri));
        } catch (qrErr) {
          setQrDataUrl(''); // manual secret is still shown
        }
      } catch (err) {
        setError(err.message || 'Could not start two-factor setup. Please try again.');
      } finally {
        setIsPreparing(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirmSetup = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setIsLoading(true);
    setError('');
    try {
      const res = await apiService.twoFactorConfirmSetup(challengeToken, code);
      if (res.success && res.data?.user) {
        setPendingResult({ success: true, data: res.data.user });
        setRecoveryCodes(Array.isArray(res.data.recoveryCodes) ? res.data.recoveryCodes : []);
        setPhase('recovery');
      } else {
        setCode('');
        handleFailure(res);
      }
    } catch (err) {
      setError(err.message || 'Verification failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const res = useRecovery
        ? await apiService.twoFactorVerify(challengeToken, { recoveryCode: recoveryCode.trim() })
        : await apiService.twoFactorVerify(challengeToken, { code });
      if (res.success && res.data) {
        onSuccess({ success: true, data: res.data });
      } else {
        setCode('');
        setRecoveryCode('');
        handleFailure(res);
      }
    } catch (err) {
      setError(err.message || 'Verification failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const copyValue = async (value, key) => {
    try {
      await copyTextToClipboard(value);
      setCopied(key);
      setTimeout(() => setCopied(''), 2000);
    } catch (err) {
      // clipboard unavailable; user can still select the text
    }
  };

  const downloadRecoveryCodes = () => {
    const text =
      `Partner portal recovery codes (${affiliateCode || 'account'})\n` +
      'Each code can be used once if you lose access to your authenticator app.\n\n' +
      recoveryCodes.join('\n') +
      '\n';
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'partner-recovery-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const submitLabel = (label) =>
    isLoading ? (
      <>
        <NewtonsCradleLoader size="28px" color="#ffffff" />
        <span>Verifying...</span>
      </>
    ) : (
      label
    );

  const backLink = (
    <button type="button" className="auth-login__2fa-link" onClick={() => onBack()}>
      <ArrowLeft size={14} aria-hidden="true" /> Back to sign in
    </button>
  );

  if (phase === 'recovery') {
    return (
      <div className="auth-login__card">
        <div className="auth-login__card-header">
          <h1>Save your recovery codes</h1>
          <p>
            Keep these codes somewhere safe. Each one can be used once to sign in if you lose access to your
            authenticator app. They will not be shown again.
          </p>
        </div>

        <div className="auth-login__form">
          <div className="auth-login__2fa-codes" aria-label="Recovery codes">
            {recoveryCodes.map((c) => (
              <code key={c}>{c}</code>
            ))}
          </div>

          <div className="auth-login__2fa-actions">
            <button type="button" className="auth-login__2fa-secondary" onClick={() => copyValue(recoveryCodes.join('\n'), 'codes')}>
              <Copy size={14} aria-hidden="true" /> {copied === 'codes' ? 'Copied' : 'Copy'}
            </button>
            <button type="button" className="auth-login__2fa-secondary" onClick={downloadRecoveryCodes}>
              <Download size={14} aria-hidden="true" /> Download
            </button>
          </div>

          <label className="auth-login__remember">
            <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
            <span>I saved these codes</span>
          </label>

          <button
            type="button"
            disabled={!saved}
            className="auth-login__submit"
            onClick={() => onSuccess(pendingResult)}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'setup') {
    return (
      <div className="auth-login__card">
        <div className="auth-login__card-header">
          <h1>Set up two-factor authentication</h1>
          <p>Scan the QR code with Google Authenticator or Microsoft Authenticator, then enter the 6-digit code.</p>
        </div>

        <form onSubmit={handleConfirmSetup} className="auth-login__form">
          {isPreparing ? (
            <div className="auth-login__2fa-center">
              <NewtonsCradleLoader size="40px" color="#e11d23" />
            </div>
          ) : setupInfo ? (
            <>
              {qrDataUrl ? (
                <div className="auth-login__2fa-center">
                  <img className="auth-login__2fa-qr" src={qrDataUrl} alt="Authenticator QR code" width={192} height={192} />
                </div>
              ) : null}

              <div>
                <span className="auth-login__label">Can&apos;t scan? Enter this key manually</span>
                <div className="auth-login__2fa-secret">
                  <code>{setupInfo.secret.match(/.{1,4}/g)?.join(' ') || setupInfo.secret}</code>
                  <button
                    type="button"
                    className="auth-login__2fa-icon"
                    onClick={() => copyValue(setupInfo.secret, 'secret')}
                    aria-label="Copy key"
                  >
                    <Copy size={14} aria-hidden="true" />
                    {copied === 'secret' ? <span>Copied</span> : null}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="twoFactorSetupCode" className="auth-login__label">
                  6-digit code
                </label>
                <input
                  id="twoFactorSetupCode"
                  className="auth-login__input auth-login__2fa-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(digitsOnly(e.target.value))}
                  autoFocus
                  required
                />
              </div>
            </>
          ) : null}

          {error ? <div className="auth-login__error">{error}</div> : null}

          {setupInfo ? (
            <button type="submit" disabled={isLoading || code.length !== 6} className="auth-login__submit">
              {submitLabel(
                <>
                  <ShieldCheck size={16} aria-hidden="true" />
                  Verify and enable
                </>
              )}
            </button>
          ) : null}

          <div className="auth-login__2fa-footer">{backLink}</div>
        </form>
      </div>
    );
  }

  return (
    <div className="auth-login__card">
      <div className="auth-login__card-header">
        <h1>Two-factor authentication</h1>
        <p>
          {useRecovery
            ? 'Enter one of your unused recovery codes.'
            : 'Enter the 6-digit code from your authenticator app.'}
        </p>
      </div>

      <form onSubmit={handleVerify} className="auth-login__form">
        {useRecovery ? (
          <div>
            <label htmlFor="twoFactorRecoveryCode" className="auth-login__label">
              Recovery code
            </label>
            <input
              id="twoFactorRecoveryCode"
              className="auth-login__input auth-login__2fa-input"
              type="text"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              placeholder="XXXXX-XXXXX"
              value={recoveryCode}
              onChange={(e) => setRecoveryCode(e.target.value.toUpperCase().slice(0, 16))}
              autoFocus
              required
            />
          </div>
        ) : (
          <div>
            <label htmlFor="twoFactorCode" className="auth-login__label">
              Authentication code
            </label>
            <input
              id="twoFactorCode"
              className="auth-login__input auth-login__2fa-input"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(digitsOnly(e.target.value))}
              autoFocus
              required
            />
          </div>
        )}

        {error ? <div className="auth-login__error">{error}</div> : null}

        <button
          type="submit"
          disabled={isLoading || (useRecovery ? recoveryCode.trim().length < 10 : code.length !== 6)}
          className="auth-login__submit"
        >
          {submitLabel('Verify')}
        </button>

        <div className="auth-login__2fa-footer">
          <button
            type="button"
            className="auth-login__2fa-link"
            onClick={() => {
              setUseRecovery((v) => !v);
              setError('');
              setCode('');
              setRecoveryCode('');
            }}
          >
            {useRecovery ? 'Use authenticator code instead' : 'Use a recovery code instead'}
          </button>
          {backLink}
        </div>
      </form>
    </div>
  );
};

export default TwoFactorStep;
